import * as React from 'react';
import ListItem from '@mui/material/ListItem';
import { IconButton, ListItemSecondaryAction, ListItemButton, Tooltip, useTheme, Box, Stack } from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import PersonIcon from '@mui/icons-material/Person';
import { Typography } from '@mui/material';
import { Patient, Visit } from '../models/core';
import { DateTime } from 'luxon';
import { analytics } from '../utils/analytics/zipAnalytics';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import { VisitReasons } from '../utils/enums';

interface PatientListItemProps {
    patient: Patient;
    visit: Visit | null;
    isConflict?: boolean;
    onEdit?: (patient: Patient) => void;
    onOpenVisits?:(patient: Patient) => void;
    onAssign?: (patient: Patient) => void;
}

export const PatientListItem = ({ patient,visit, isConflict, onEdit, onOpenVisits, onAssign, ...props }: PatientListItemProps): JSX.Element => {
    const theme = useTheme();
    const onEditClick = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
        analytics.breadcrumb("patient_edit_select", { patientId: patient._id});
        onEdit && onEdit(patient);
        event.stopPropagation();
        event.preventDefault();
    }, [onEdit, patient]);

    const onAssignClick = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
        analytics.breadcrumb("patient_assign_select", { patientId: patient._id});
        onAssign && onAssign(patient);
        event.stopPropagation();
        event.preventDefault();
    }, [onAssign, patient]);

    const visitReasons = React.useMemo(() => {
        return (visit?.visitReasons ?? []).map(e => VisitReasons.find(t => t.code == e)?.name)?.join(', ') || 'None';
    },[visit?.visitReasons]);

    return (
        <ListItem
            sx={{
                backgroundColor: isConflict ? theme.palette.error.light + '40' : undefined
            }}
            {...props}
            >
            <ListItemButton disableRipple>
                <ListItemText
                primary={
                    <Typography component='div' sx={{ fontWeight: 500 }}>
                    {patient.lastName}, {patient.firstName} (
                    {patient.dob
                        ? DateTime.fromISO(patient.dob).toLocaleString(DateTime.DATE_SHORT)
                        : 'No DoB'}
                    )
                    </Typography>
                }
                secondary={
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'flex-start', sm: 'stretch' }}
                        sx={{
                            width: '100%',
                            justifyContent: 'space-between',
                        }}
                    >
                        {/* Patient */}
                        <Box sx={{flexBasis:'50%', flexShrink:0}}>
                            <Typography variant='body2'>
                                <strong>Patient Status:</strong> {patient?.status || 'Unknown'}
                            </Typography>
                            <Typography variant='body2'>
                                <strong>Language:</strong> {patient?.language || 'Unknown'}
                            </Typography>
                            &nbsp;
                            <Typography variant='body2'>
                                <strong>Assigned To:</strong> {patient?.assignment?.assignedTo || 'None'}
                            </Typography>
                        </Box>

                        {/* Visit */}
                        <Box sx={{flexBasis:'50%', flexShrink:0}}>
                            <Typography variant='body2'>
                                <strong>Visit Status:</strong> {visit?.status || 'Unknown'}
                            </Typography>
                            <Typography variant='body2'>
                                <strong>Visit Reasons:</strong> {visitReasons}
                            </Typography>
                            
                            <Typography variant='body2'>
                                <strong>Contact Attempts:</strong> {visit?.numContacts || '0'}
                            </Typography>
                            <Typography variant='body2'>
                                <strong>Last Contact Date:</strong> {visit?.lastContactDate || 'Unknown'}
                            </Typography>
                        </Box>
                    </Stack>
                }
                />

                <ListItemSecondaryAction>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs:2, sm:1 }}
                        alignItems={{ xs: 'flex-start', sm: 'stretch' }}
                        sx={{
                            width: '100%',
                            justifyContent: 'space-between',
                        }}
                    >
                    <Tooltip title="Edit Visits">
                        <IconButton onClick={() => {
                            analytics.breadcrumb('patient_visit_select', { patientId: patient._id });
                            onOpenVisits?.(patient);
                        }}
                        >
                        <MedicalServicesOutlinedIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Assign">
                        <IconButton onClick={onAssignClick}>
                        <AssignmentIndIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Edit Patient">
                        <IconButton onClick={onEditClick}>
                        <PersonIcon />
                        </IconButton>
                    </Tooltip>
                    </Stack>
                </ListItemSecondaryAction>

            </ListItemButton>
        </ListItem>
    );
};
